import React, { useEffect, useState } from 'react';
import { X, Info, ChevronDown, ChevronUp, Check, Calendar, Clock } from 'lucide-react';
interface ScheduleReportsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (schedule: any) => void;
  initialData?: any;
}
export function ScheduleReportsModal({
  isOpen,
  onClose,
  onSave,
  initialData
}: ScheduleReportsModalProps) {
  const [reportType, setReportType] = useState('Member Summary Report');
  const [frequency, setFrequency] = useState('Weekly');
  const [dayOfWeek, setDayOfWeek] = useState('Monday');
  const [dayOfMonth, setDayOfMonth] = useState('1st');
  const [timeHour, setTimeHour] = useState('8');
  const [timeAmPm, setTimeAmPm] = useState('AM');
  const [recipients, setRecipients] = useState<string[]>(['user@example.com']);
  const [newRecipient, setNewRecipient] = useState('');
  const [recipientError, setRecipientError] = useState('');
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [includeSummary, setIncludeSummary] = useState(true);
  const [includeComparison, setIncludeComparison] = useState(true);
  const [includeDetails, setIncludeDetails] = useState(false);
  const [fileFormat, setFileFormat] = useState('CSV');
  const [isSaving, setIsSaving] = useState(false);
  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        // Populate from initialData if editing
        setReportType(initialData.reportType || 'Member Summary Report');
        setFrequency(initialData.frequency || 'Weekly');
        // ... set other fields
      } else {
        // Reset to defaults
        setReportType('Member Summary Report');
        setFrequency('Weekly');
        setDayOfWeek('Monday');
        setDayOfMonth('1st');
        setTimeHour('8');
        setTimeAmPm('AM');
        setRecipients(['user@example.com']); // In real app, use current user email
        setNewRecipient('');
        setRecipientError('');
        setIsAdvancedOpen(false);
        setIncludeSummary(true);
        setIncludeComparison(true);
        setIncludeDetails(false);
        setFileFormat('CSV');
      }
      setIsSaving(false);
    }
  }, [isOpen, initialData]);
  if (!isOpen) return null;
  const handleAddRecipient = (e?: React.KeyboardEvent) => {
    if (e && e.key !== 'Enter') return;
    e?.preventDefault();
    const email = newRecipient.trim();
    if (!email) return;
    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setRecipientError('Please enter a valid email address');
      return;
    }
    if (recipients.length >= 5) {
      setRecipientError('Maximum 5 recipients allowed');
      return;
    }
    if (recipients.includes(email)) {
      setRecipientError('Email already added');
      return;
    }
    setRecipients([...recipients, email]);
    setNewRecipient('');
    setRecipientError('');
  };
  const removeRecipient = (emailToRemove: string) => {
    setRecipients(recipients.filter(email => email !== emailToRemove));
  };
  const handleSave = () => {
    if (recipients.length === 0) {
      setRecipientError('At least one recipient is required');
      return;
    }
    setIsSaving(true);
    // Simulate API call
    setTimeout(() => {
      onSave({
        reportType,
        frequency,
        schedule: {
          dayOfWeek: frequency === 'Weekly' ? dayOfWeek : undefined,
          dayOfMonth: frequency === 'Monthly' || frequency === 'Quarterly' ? dayOfMonth : undefined,
          time: `${timeHour}:00 ${timeAmPm}`
        },
        recipients,
        options: {
          includeSummary,
          includeComparison,
          includeDetails,
          fileFormat
        }
      });
      setIsSaving(false);
      onClose();
    }, 1500);
  };
  const getReportDescription = () => {
    switch (reportType) {
      case 'Member Summary Report':
        return 'Includes member name, email, subscription status, tier, join date, lifetime value, and last order date.';
      case 'Revenue Report':
        return 'Detailed breakdown of revenue by product, subscription tier, and customer segments over time.';
      case 'Payment Issues Report':
        return 'List of all failed payments, retry attempts, and at-risk subscriptions requiring attention.';
      case 'Cancellation Report':
        return 'Analysis of cancelled subscriptions including reasons, churn dates, and customer tenure.';
      default:
        return 'Standard report containing key metrics and data points for the selected category.';
    }
  };
  return <div className="fixed inset-0 z-[1050] flex items-center justify-center" data-id="element-4502" data-mp="17euxgdqhn9bt">
      {/* Overlay */}
      <div className="absolute inset-0 bg-[#0F172A]/50 backdrop-blur-sm transition-opacity" onClick={onClose} data-id="element-4503" data-mp="1cdeimbx9ch34" />

      {/* Modal Container */}
      <div className="relative w-full max-w-[560px] bg-white rounded-lg shadow-[0_20px_25px_rgba(15,23,42,0.15)] flex flex-col max-h-[90vh] animate-in fade-in zoom-in-95 duration-200" data-id="element-4504" data-mp="3979l7tfpr805">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-[#E2E8F0] flex-shrink-0" data-id="element-4505" data-mp="1vbhumwiux3gn">
          <div className="flex items-start justify-between" data-id="element-4506" data-mp="1qieuu6nhkxs0">
            <div data-id="element-4507" data-mp="1mhd4kfbjrw26">
              <h2 className="text-[20px] font-semibold text-[#0F172A] leading-tight" data-id="element-4508" data-mp="1mgtol45gjhqs">
                {initialData ? 'Edit Scheduled Report' : 'Schedule Report'}
              </h2>
              <p className="text-[13px] text-[#64748B] mt-1" data-id="element-4509" data-mp="2e8ixobsuothy">
                Automatically receive reports in your inbox
              </p>
            </div>
            <button onClick={onClose} className="p-1.5 text-[#94A3B8] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded-md transition-colors" aria-label="Close modal" data-id="element-4510" data-mp="2a7z0j88a1pvt">
              <X className="w-5 h-5" data-id="element-4511" data-mp="207yfnhurf1pi" />
            </button>
          </div>
        </div>

        {/* Body - Scrollable */}
        <div className="p-6 overflow-y-auto flex-1 space-y-6" data-id="element-4512" data-mp="tuim1nyaj6rc">
          {/* Section 1: Report Type */}
          <div data-id="element-4513" data-mp="osjioe2jw9ue">
            <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-4514" data-mp="ovxjvizhad09">
              Report Type
            </label>
            <div className="relative" data-id="element-4515" data-mp="vampmxd4aerr">
              <select value={reportType} onChange={e => setReportType(e.target.value)} className="w-full h-10 pl-3 pr-10 bg-white border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none transition-all appearance-none" disabled={isSaving} data-id="element-4516" data-mp="v9l0xq36htrk">
                <option data-id="element-4517" data-mp="7ifmnfcvz9jd">Member Summary Report</option>
                <option data-id="element-4518" data-mp="cbig7nhnyizk">Revenue Report</option>
                <option data-id="element-4519" data-mp="cdbspjeyjxfh">Payment Issues Report</option>
                <option data-id="element-4520" data-mp="3uq19jgp1aoq0">Cancellation Report</option>
                <option data-id="element-4521" data-mp="3unez99ardv43">Subscription Activity Report</option>
                <option data-id="element-4522" data-mp="2m8s0ipxxkgq">Churn Analysis Report</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8] pointer-events-none" data-id="element-4523" data-mp="3kqsfyoy2zc46" />
            </div>
            <div className="mt-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-md p-3 flex gap-2" data-id="element-4524" data-mp="3ppdgkph8vxt5">
              <Info className="w-4 h-4 text-[#94A3B8] flex-shrink-0 mt-0.5" data-id="element-4525" data-mp="364lmuck7gtjf" />
              <p className="text-[13px] text-[#64748B] leading-relaxed" data-id="element-4526" data-mp="316k45sqoyw8h">
                {getReportDescription()}
              </p>
            </div>
          </div>

          {/* Section 2: Frequency */}
          <div data-id="element-4527" data-mp="3b2n2somc4cqo">
            <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-4528" data-mp="3b251hdktf8bw">
              Frequency
            </label>
            <div className="flex flex-wrap gap-2 mb-4" data-id="element-4529" data-mp="3k44ii5vtyceu">
              {['Daily', 'Weekly', 'Monthly', 'Quarterly'].map(option => <button key={option} onClick={() => setFrequency(option)} disabled={isSaving} className={`
                    h-10 px-4 rounded-md text-[14px] font-medium transition-all border
                    ${frequency === option ? 'bg-[#E0F2FE] border-[#0EA5E9] text-[#0EA5E9]' : 'bg-white border-[#CBD5E1] text-[#475569] hover:bg-[#F8FAFC]'}
                  `} data-id="element-4530" data-mp="2o672gdhrv0i">
                  {option}
                </button>)}
            </div>

            <div className="grid grid-cols-2 gap-4" data-id="element-4531" data-mp="1e15ormp2yu4z">
              {/* Conditional Frequency Fields */}
              {frequency === 'Weekly' && <div data-id="element-4532" data-mp="1nzvajqk3kyqi">
                  <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-4533" data-mp="1nzbt0ayd0cd0">
                    Day of Week
                  </label>
                  <div className="relative" data-id="element-4534" data-mp="1s0nk6kwvt5nb">
                    <select value={dayOfWeek} onChange={e => setDayOfWeek(e.target.value)} className="w-full h-10 pl-3 pr-10 bg-white border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none appearance-none" disabled={isSaving} data-id="element-4535" data-mp="vbpp11l7z5pm">
                      {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => <option key={day} value={day} data-id="element-4536" data-mp="cl7z14rruo7y">
                          {day}
                        </option>)}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8] pointer-events-none" data-id="element-4537" data-mp="gkqg1pgh2f5i" />
                  </div>
                </div>}

              {(frequency === 'Monthly' || frequency === 'Quarterly') && <div data-id="element-4538" data-mp="22ij3f9uhz4">
                  <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-4539" data-mp="1ko8cc66dtu">
                    Day of Month
                  </label>
                  <div className="relative" data-id="element-4540" data-mp="3r83wbjqmfpog">
                    <select value={dayOfMonth} onChange={e => setDayOfMonth(e.target.value)} className="w-full h-10 pl-3 pr-10 bg-white border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none appearance-none" disabled={isSaving} data-id="element-4541" data-mp="3m9u67njr7f9p">
                      {Array.from({
                    length: 28
                  }, (_, i) => i + 1).map(day => <option key={day} value={`${day}${getOrdinal(day)}`} data-id="element-4542" data-mp="33j2mpt61y8hl">
                          {day}
                          {getOrdinal(day)}
                        </option>)}
                      <option value="Last day" data-id="element-4543" data-mp="1a3d44ojf0g89">Last day</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8] pointer-events-none" data-id="element-4544" data-mp="1629qm8ouawa7" />
                  </div>
                </div>}

              {/* Time Selection - Always visible */}
              <div className={frequency === 'Daily' ? 'col-span-2' : 'col-span-1'} data-id="element-4545" data-mp="1evd0k70a0r03">
                <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-4546" data-mp="r47om2xgiiv2">
                  Time
                </label>
                <div className="flex gap-2" data-id="element-4547" data-mp="n4qh73ygzbn9">
                  <div className="relative flex-1" data-id="element-4548" data-mp="112ney3onto3i">
                    <select value={timeHour} onChange={e => setTimeHour(e.target.value)} className="w-full h-10 pl-3 pr-10 bg-white border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none appearance-none" disabled={isSaving} data-id="element-4549" data-mp="114hzvhzxlg7l">
                      {Array.from({
                      length: 12
                    }, (_, i) => i + 1).map(hour => <option key={hour} value={hour} data-id="element-4550" data-mp="3pfavrqu21nxv">
                          {hour}:00
                        </option>)}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8] pointer-events-none" data-id="element-4551" data-mp="3ggh2a9c0tdoj" />
                  </div>
                  <div className="relative w-[80px]" data-id="element-4552" data-mp="3lh4tow45vwja">
                    <select value={timeAmPm} onChange={e => setTimeAmPm(e.target.value)} className="w-full h-10 pl-3 pr-10 bg-white border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none appearance-none" disabled={isSaving} data-id="element-4553" data-mp="cyyzwhf0oyvi">
                      <option value="AM" data-id="element-4554" data-mp="87gy5r566u9h">AM</option>
                      <option value="PM" data-id="element-4555" data-mp="865vxvrdhukh">PM</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8] pointer-events-none" data-id="element-4556" data-mp="vw04400qyuye" />
                  </div>
                </div>
              </div>
            </div>

            {frequency === 'Quarterly' && <p className="text-[12px] text-[#64748B] mt-2 bg-[#F8FAFC] px-3 py-2 rounded border border-[#E2E8F0]" data-id="element-4557" data-mp="15l5q5464kzdw">
                Reports sent in January, April, July, October
              </p>}

            <p className="text-[12px] text-[#94A3B8] mt-2" data-id="element-4558" data-mp="vo0ywpxfadq8">
              Reports are generated in your store's timezone (AEST)
            </p>
          </div>

          {/* Section 3: Recipients */}
          <div data-id="element-4559" data-mp="munzr68rmfvp">
            <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-4560" data-mp="mu668hdhmfrh">
              Send to
            </label>
            <div className={`
                min-h-10 px-2 py-1 bg-white border rounded-md flex flex-wrap gap-2 items-center
                focus-within:border-[#0EA5E9] focus-within:ring-2 focus-within:ring-[#0EA5E9]/20 transition-all
                ${recipientError ? 'border-[#EF4444]' : 'border-[#CBD5E1]'}
              `} data-id="element-4561" data-mp="hwmjet2q4unx">
              {recipients.map(email => <span key={email} className="inline-flex items-center gap-1 px-2 py-1 bg-[#E0F2FE] text-[#0284C7] text-[13px] rounded-md" data-id="element-4562" data-mp="2fk8695jh3bwv">
                  {email}
                  <button onClick={() => removeRecipient(email)} className="hover:text-[#0369A1] focus:outline-none" disabled={isSaving} data-id="element-4563" data-mp="2jn4upcvp228q">
                    <X className="w-3.5 h-3.5" data-id="element-4564" data-mp="2tjq0i0fjfhdm" />
                  </button>
                </span>)}
              <input type="text" value={newRecipient} onChange={e => {
              setNewRecipient(e.target.value);
              setRecipientError('');
            }} onKeyDown={handleAddRecipient} onBlur={() => {
              if (newRecipient) handleAddRecipient();
            }} placeholder={recipients.length === 0 ? 'Enter email addresses...' : ''} className="flex-1 min-w-[120px] h-8 outline-none text-[14px] text-[#0F172A] placeholder:text-[#94A3B8]" disabled={isSaving} data-id="element-4565" data-mp="1vwh7lkd7j61l" />
            </div>
            {recipientError && <p className="text-[12px] text-[#EF4444] mt-1" data-id="element-4566" data-mp="1i2zzcvpz1fqv">
                {recipientError}
              </p>}
            <p className="text-[12px] text-[#94A3B8] mt-1" data-id="element-4567" data-mp="3ju03012hgheb">
              Press Enter to add multiple recipients
            </p>
          </div>

          {/* Section 4: Advanced Options */}
          <div className="border-t border-[#E2E8F0] pt-4" data-id="element-4568" data-mp="3tk1r2suu5zt8">
            <button onClick={() => setIsAdvancedOpen(!isAdvancedOpen)} className="flex items-center gap-2 text-[14px] font-medium text-[#475569] hover:text-[#0F172A] transition-colors" type="button" data-id="element-4569" data-mp="3ostgvulomaf0">
              {isAdvancedOpen ? <ChevronUp className="w-4 h-4" data-id="element-4570" data-mp="2ds3wumko4xg" /> : <ChevronDown className="w-4 h-4" data-id="element-4571" data-mp="2cpcmllc80h0" />}
              Advanced Options
            </button>

            {isAdvancedOpen && <div className="mt-4 space-y-4 animate-in slide-in-from-top-2 duration-200" data-id="element-4572" data-mp="gadwbq3o51kh">
                <div className="space-y-3" data-id="element-4573" data-mp="bbu97mdc9jqj">
                  <label className="flex items-center gap-3 cursor-pointer group" data-id="element-4574" data-mp="2i3bkv9qex7zm">
                    <input type="checkbox" checked={includeSummary} onChange={e => setIncludeSummary(e.target.checked)} className="w-[18px] h-[18px] rounded border-[#CBD5E1] text-[#0EA5E9] focus:ring-[#0EA5E9] cursor-pointer" disabled={isSaving} data-id="element-4575" data-mp="2d5js6dhy1hud" />
                    <span className="text-[14px] text-[#475569] group-hover:text-[#0F172A]" data-id="element-4576" data-mp="xlarp4pypp3m">
                      Include summary statistics at top
                    </span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer group" data-id="element-4577" data-mp="nug5g0cpupi6">
                    <input type="checkbox" checked={includeComparison} onChange={e => setIncludeComparison(e.target.checked)} className="w-[18px] h-[18px] rounded border-[#CBD5E1] text-[#0EA5E9] focus:ring-[#0EA5E9] cursor-pointer" disabled={isSaving} data-id="element-4578" data-mp="9vq9io1muvve" />
                    <span className="text-[14px] text-[#475569] group-hover:text-[#0F172A]" data-id="element-4579" data-mp="4x55edj9fs1o">
                      Include comparison to previous period
                    </span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer group" data-id="element-4580" data-mp="1zb66z6672t5g">
                    <input type="checkbox" checked={includeDetails} onChange={e => setIncludeDetails(e.target.checked)} className="w-[18px] h-[18px] rounded border-[#CBD5E1] text-[#0EA5E9] focus:ring-[#0EA5E9] cursor-pointer" disabled={isSaving} data-id="element-4581" data-mp="388cy45vmii1k" />
                    <span className="text-[14px] text-[#475569] group-hover:text-[#0F172A]" data-id="element-4582" data-mp="2z8hhvmn7nwuo">
                      Include individual member details
                    </span>
                  </label>
                </div>

                <div className="pt-2" data-id="element-4583" data-mp="3qy2q6l804pps">
                  <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-4584" data-mp="3mxiy3w2accai">
                    File Format
                  </label>
                  <div className="flex gap-4" data-id="element-4585" data-mp="3i4zjqwvrxd4n">
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-4586" data-mp="3d7pj0l2xpf1i">
                      <input type="radio" name="fileFormat" value="CSV" checked={fileFormat === 'CSV'} onChange={e => setFileFormat(e.target.value)} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] focus:ring-[#0EA5E9]" disabled={isSaving} data-id="element-4587" data-mp="3d5muy6lcdvzf" />
                      <span className="text-[14px] text-[#475569]" data-id="element-4588" data-mp="h2x74nb7mw5z">CSV</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-4589" data-mp="m21dz7anwht5">
                      <input type="radio" name="fileFormat" value="Excel" checked={fileFormat === 'Excel'} onChange={e => setFileFormat(e.target.value)} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] focus:ring-[#0EA5E9]" disabled={isSaving} data-id="element-4590" data-mp="qzjq16jz7d5d" />
                      <span className="text-[14px] text-[#475569]" data-id="element-4591" data-mp="100hmn3s34idx">
                        Excel (.xlsx)
                      </span>
                    </label>
                  </div>
                </div>
              </div>}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-[#F8FAFC] border-t border-[#E2E8F0] rounded-b-lg flex justify-between items-center flex-shrink-0" data-id="element-4592" data-mp="1eoceyj26kkqh">
          <div data-id="element-4593" data-mp="1ipeez23x90ti">
            {initialData && <button className="text-[14px] font-medium text-[#EF4444] hover:text-[#DC2626] transition-colors" disabled={isSaving} data-id="element-4594" data-mp="1npsqiu4qeswa">
                Delete Schedule
              </button>}
          </div>
          <div className="flex gap-3" data-id="element-4595" data-mp="242phbfvwo9oe">
            <button onClick={onClose} className="h-10 px-5 border border-[#CBD5E1] rounded-md text-[14px] font-medium text-[#475569] hover:bg-white hover:border-[#94A3B8] transition-all bg-white" disabled={isSaving} data-id="element-4596" data-mp="241o5vaaz9l65">
              Cancel
            </button>
            <button onClick={handleSave} disabled={isSaving} className="h-10 px-5 bg-[#0EA5E9] rounded-md text-[14px] font-medium text-white hover:bg-[#0284C7] transition-all shadow-sm flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-4597" data-mp="1qa9fgieqqhp2">
              {isSaving ? <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-4598" data-mp="1v9ce9l44nrol" />
                  Saving...
                </> : <>{initialData ? 'Update Schedule' : 'Save Schedule'}</>}
            </button>
          </div>
        </div>
      </div>
    </div>;
}
// Helper for ordinal suffixes
function getOrdinal(n: number) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}